// components/charts/ESIMStatusChart.js
import React, { useMemo } from "react";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const ESIMStatusChart = ({ orders, loading }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Custom colors that match your theme
  const COLORS = [
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.secondary.main,
  ];

  const statusData = useMemo(() => {
    if (!orders || orders.length === 0) return [];

    // Count orders by status
    const statusCounts = orders.reduce((acc, order) => {
      const status = order.status || "UNKNOWN";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    // Convert to array for chart
    return Object.entries(statusCounts).map(([status, count]) => ({
      name: t(`common.orderStates.${status.toLowerCase()}.label`, status),
      value: count,
      status,
    }));
  }, [orders, t]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            bgcolor: "background.paper",
            p: 2,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            boxShadow: theme.shadows[3],
          }}
        >
          <Typography variant="subtitle2">{payload[0].payload.name}</Typography>
          <Typography variant="body2">
            {t("dashboard.count")}: <b>{payload[0].value}</b>
          </Typography>
          <Typography variant="body2">
            {t("dashboard.percentage")}:{" "}
            <b>{((payload[0].value / orders.length) * 100).toFixed(1)}%</b>
          </Typography>
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={300}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (statusData.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={300}
      >
        <Typography color="textSecondary">
          {t("dashboard.noESIMData")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box height={300}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={statusData}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={80}
            paddingAngle={5}
            dataKey="value"
            animationDuration={1000}
            animationBegin={200}
          >
            {statusData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
                stroke={theme.palette.background.paper}
                strokeWidth={2}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="bottom"
            height={36}
            formatter={(value) => (
              <span
                style={{
                  fontSize: "0.875rem",
                  color: theme.palette.text.primary,
                }}
              >
                {value}
              </span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default ESIMStatusChart;
